import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TablePagination,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  Balance as BalanceIcon,
  History as HistoryIcon,
  AddCircleOutline as StockInIcon,
  RemoveCircleOutline as StockOutIcon,
  TuneOutlined as AdjustIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  useCrateStocks,
  useCrateStockStats,
  useCrateStockHistory,
  useCreateCrateStock,
  useCreateCrateStockOnly,
  useUpdateCrateStock,
  useDeleteCrateStock,
} from '../hooks/useCrateStocks';
import { useSuppliers } from '../hooks/useFeed';
import { format } from 'date-fns';

// ─── design tokens ─────────────────────────────────────────────────────────────

const dataGridSx = {
  border: 0,
  background: 'transparent',
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '& .MuiDataGrid-columnHeaders': {
    background: 'rgba(255,152,0,0.05)',
    borderBottom: '1px solid rgba(255,152,0,0.12)',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.7px',
    color: 'rgba(255,152,0,0.6)',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-row:hover': { background: 'rgba(255,152,0,0.04)' },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid rgba(255,152,0,0.1)',
    background: 'rgba(255,152,0,0.03)',
  },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
    { color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' },
  '& .MuiTablePagination-select': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiIconButton-root.Mui-disabled': { color: 'rgba(255,255,255,0.15)' },
  '& .MuiDataGrid-virtualScroller': { minHeight: 160 },
};

const dialogPaper = {
  borderRadius: 3,
  background: '#0e0e10',
  backgroundImage: 'none',
  border: '1px solid rgba(255,152,0,0.2)',
};

// ─── Event metadata ────────────────────────────────────────────────────────────

const EVENT_META: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  purchased: { label: 'Purchased', color: '#66bb6a', icon: <StockInIcon fontSize="small" /> },
  adjusted: { label: 'Adjusted', color: '#42a5f5', icon: <AdjustIcon fontSize="small" /> },
  removed: { label: 'Removed', color: '#ef5350', icon: <StockOutIcon fontSize="small" /> },
  used_in_sale: {
    label: 'Used in Sale',
    color: '#ff9800',
    icon: <StockOutIcon fontSize="small" />,
  },
};

// ─── Form defaults ─────────────────────────────────────────────────────────────

const emptyForm = {
  quantity: '',
  unit_price: '',
  supplier: '',
  supplier_id: null as number | null,
  is_credit: false,
  notes: '',
  purchased_date: new Date().toISOString().split('T')[0],
  skip_expenditure: false,
};

// ─── Page ──────────────────────────────────────────────────────────────────────

const CrateStocks: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editRecord, setEditRecord] = useState<any | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [formError, setFormError] = useState('');

  const { data: stocksData, isFetching } = useCrateStocks({ page: page + 1, per_page: pageSize });
  const { data: stats } = useCrateStockStats();
  const { data: historyData, isFetching: historyFetching } = useCrateStockHistory({
    page: historyPage + 1,
    per_page: historyPageSize,
  });
  const createMutation = useCreateCrateStock();
  const createStockOnlyMutation = useCreateCrateStockOnly();
  const updateMutation = useUpdateCrateStock();
  const deleteMutation = useDeleteCrateStock();
  const { data: suppliers = [] } = useSuppliers();

  const rows: any[] = stocksData?.data ?? [];
  const total: number = stocksData?.total ?? 0;
  const balance = stats?.balance ?? 0;
  const balanceColor = balance > 50 ? '#66bb6a' : balance > 10 ? '#ff9800' : '#ef5350';

  const isMutating =
    createMutation.isPending ||
    createStockOnlyMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  const openCreate = () => {
    setEditRecord(null);
    setForm({ ...emptyForm });
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (row: any) => {
    setEditRecord(row);
    setForm({
      quantity: String(row.quantity / 100),
      unit_price: String(parseFloat(String(row.unit_price).replace(/[^0-9.]/g, '')) * 100),
      supplier: row.supplier ?? '',
      supplier_id: row.supplier_id ?? null,
      is_credit: row.is_credit ?? false,
      notes: row.notes ?? '',
      purchased_date: row.purchased_date
        ? new Date(row.purchased_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      skip_expenditure: row.skipped_expenditure ?? false,
    });
    setFormError('');
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setFormError('');
    if (!form.quantity || !form.unit_price || !form.purchased_date) {
      setFormError('Quantity, unit price and date are required.');
      return;
    }
    const payload = {
      quantity: parseInt(form.quantity, 10) * 100,
      unit_price: parseFloat(form.unit_price) / 100,
      supplier: form.supplier || null,
      supplier_id: form.supplier_id || undefined,
      is_credit: form.is_credit,
      notes: form.notes || null,
      purchased_date: form.purchased_date,
    };
    try {
      if (editRecord) {
        await updateMutation.mutateAsync({ id: editRecord.id, data: payload });
      } else if (form.skip_expenditure) {
        await createStockOnlyMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }
      setDialogOpen(false);
    } catch (e: any) {
      setFormError(e?.response?.data?.message ?? 'An error occurred.');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteMutation.mutateAsync(deleteId);
    setDeleteId(null);
  };

  // ── columns ───────────────────────────────────────────────────────────────

  const columns: GridColDef[] = [
    {
      field: 'purchased_date',
      headerName: 'Date',
      width: 120,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
          {row.purchased_date ? format(new Date(row.purchased_date), 'dd MMM yyyy') : '—'}
        </Typography>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Crates',
      width: 100,
      renderCell: ({ row }) => (
        <Typography variant="body2" fontWeight={700} sx={{ color: '#ff9800', fontSize: '0.85rem' }}>
          {(row.quantity / 100).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'unit_price',
      headerName: 'Unit Price',
      width: 130,
      renderCell: ({ row }) => (
        <Typography
          variant="body2"
          sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace', fontSize: '0.8rem' }}
        >
          {row.unit_price}
        </Typography>
      ),
    },
    {
      field: 'total_cost',
      headerName: 'Total Cost',
      width: 140,
      renderCell: ({ row }) => (
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ color: '#42a5f5', fontFamily: 'monospace', fontSize: '0.8rem' }}
        >
          {row.total_cost}
        </Typography>
      ),
    },
    {
      field: 'is_credit',
      headerName: 'Type',
      width: 85,
      renderCell: ({ row }) =>
        row.is_credit ? (
          <Chip
            label="Credit"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.62rem',
              fontWeight: 700,
              bgcolor: 'rgba(33,150,243,0.12)',
              color: '#42a5f5',
              border: '1px solid rgba(33,150,243,0.3)',
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
            Cash
          </Typography>
        ),
    },
    {
      field: 'supplier',
      headerName: 'Supplier',
      flex: 1,
      minWidth: 130,
      renderCell: ({ row }) =>
        row.supplier ? (
          <Chip
            label={row.supplier}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.68rem',
              fontWeight: 600,
              bgcolor: 'rgba(255,152,0,0.1)',
              color: '#ff9800',
              border: '1px solid rgba(255,152,0,0.2)',
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.8rem' }}>
            —
          </Typography>
        ),
    },
    {
      field: 'purchased_by',
      headerName: 'Recorded By',
      width: 145,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem' }}>
          {row.purchased_by?.name ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => (
        <Typography
          variant="body2"
          sx={{
            color: row.notes ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.15)',
            fontSize: '0.78rem',
            fontStyle: row.notes ? 'normal' : 'normal',
          }}
        >
          {row.notes ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={0.25} height="100%">
          <Tooltip title="Edit" arrow>
            <IconButton
              size="small"
              onClick={() => openEdit(row)}
              sx={{
                color: 'rgba(255,152,0,0.7)',
                '&:hover': { color: '#ff9800', bgcolor: 'rgba(255,152,0,0.1)' },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              onClick={() => setDeleteId(row.id)}
              sx={{
                color: 'rgba(244,67,54,0.6)',
                '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.08)' },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const totalCrates = parseInt(form.quantity || '0', 10) * 100;
  const totalCost = parseFloat(form.unit_price || '0') * parseInt(form.quantity || '0', 10);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(isFetching || historyFetching || isMutating) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffcc02 50%, #ff9800 100%)',
            },
          }}
        />
      )}

      {/* ── Header ── */}
      <Box
        sx={{
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0e0e10 0%, #1a1a1d 60%, #0e0e10 100%)',
          border: '1px solid rgba(255,152,0,0.12)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,152,0,0.06) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            p: { xs: 2.5, md: 3 },
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            position: 'relative',
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              flexShrink: 0,
              background:
                'linear-gradient(135deg, rgba(255,152,0,0.18) 0%, rgba(255,152,0,0.06) 100%)',
              border: '1px solid rgba(255,152,0,0.22)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InventoryIcon sx={{ color: '#ff9800', fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                color: '#ffffff',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                fontSize: { xs: '1.5rem', md: '1.9rem' },
              }}
            >
              Crate Management
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.38)', mt: 0.4 }}>
              Track crate purchases and monitor stock available for egg sales
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              borderRadius: 2,
              px: 2.5,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              boxShadow: '0 4px 18px rgba(255,152,0,0.32)',
              '&:hover': { background: 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)' },
            }}
          >
            Record Purchase
          </Button>
        </Box>
      </Box>

      {/* ── Stats Strip ── */}
      <Box display="flex" gap={2} mb={4} flexWrap="wrap">
        {[
          {
            label: 'Total Purchased',
            value: stats?.total_purchased?.toLocaleString() ?? '—',
            sub: 'all time',
            color: '#42a5f5',
            Icon: TrendingUpIcon,
          },
          {
            label: 'Total Used',
            value: stats?.total_used?.toLocaleString() ?? '—',
            sub: 'via egg sales',
            color: '#ff9800',
            Icon: ShoppingCartIcon,
          },
          {
            label: 'Current Balance',
            value: balance.toLocaleString(),
            sub: 'crates in stock',
            color: balanceColor,
            Icon: BalanceIcon,
          },
          {
            label: 'Total Spend',
            value: `GH₵ ${Number(stats?.total_spend ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
            sub: 'on crate purchases',
            color: '#ab47bc',
            Icon: InventoryIcon,
          },
        ].map(({ label, value, sub, color, Icon }) => (
          <Paper
            key={label}
            elevation={0}
            sx={{
              flex: 1,
              minWidth: 180,
              p: 2.5,
              background: '#111318',
              border: '1px solid rgba(255,255,255,0.05)',
              borderLeft: `3px solid ${color}`,
              borderRadius: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform 0.18s, box-shadow 0.18s',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 12px 36px rgba(0,0,0,0.55), 0 0 0 1px ${color}22`,
              },
            }}
          >
            <Icon
              sx={{
                position: 'absolute',
                right: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 56,
                color: `${color}0D`,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.35)',
                letterSpacing: '0.7px',
                fontSize: '0.62rem',
                textTransform: 'uppercase',
                display: 'block',
                mb: 0.5,
              }}
            >
              {label}
            </Typography>
            <Typography
              fontWeight={800}
              sx={{
                color,
                lineHeight: 1,
                mb: 0.4,
                fontSize:
                  typeof value === 'string' && value.startsWith('GH') ? '1.05rem' : '1.6rem',
              }}
            >
              {value}
            </Typography>
            {sub && (
              <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.68rem' }}>
                {sub}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>

      {/* ── Low stock warning ── */}
      {balance <= 10 && stats !== undefined && (
        <Alert
          icon={<WarningIcon />}
          severity={balance <= 0 ? 'error' : 'warning'}
          sx={{
            mb: 3,
            borderRadius: 2,
            bgcolor: balance <= 0 ? 'rgba(211,47,47,0.1)' : 'rgba(255,152,0,0.08)',
            border: `1px solid ${balance <= 0 ? 'rgba(211,47,47,0.3)' : 'rgba(255,152,0,0.25)'}`,
            color: '#ffffff',
            '& .MuiAlert-icon': { color: balance <= 0 ? '#ef5350' : '#ff9800' },
          }}
        >
          {balance <= 0
            ? 'Out of crates — purchase more to continue recording egg sales.'
            : `Low crate stock — only ${balance} crate${balance === 1 ? '' : 's'} remaining.`}
        </Alert>
      )}

      {/* ── Tabs Panel ── */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid rgba(255,152,0,0.12)',
          borderRadius: 2.5,
          overflow: 'hidden',
          background: '#0e0e10',
        }}
      >
        <Box
          sx={{
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            px: 2,
            background: 'linear-gradient(90deg, rgba(255,152,0,0.05) 0%, transparent 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.4)',
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 48,
                fontSize: '0.82rem',
              },
              '& .Mui-selected': { color: '#ff9800' },
              '& .MuiTabs-indicator': { backgroundColor: '#ff9800', height: 2 },
            }}
          >
            <Tab
              icon={<InventoryIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label={`Purchases (${total})`}
            />
            <Tab
              icon={<HistoryIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label={`Stock History (${historyData?.total ?? 0})`}
            />
          </Tabs>
        </Box>

        {/* Purchases tab */}
        {tab === 0 && (
          <DataGrid
            rows={rows}
            columns={columns}
            rowCount={total}
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={({ page: p, pageSize: ps }) => {
              setPage(p);
              setPageSize(ps);
            }}
            pageSizeOptions={[10, 15, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            rowHeight={52}
            sx={dataGridSx}
          />
        )}

        {/* History tab */}
        {tab === 1 && (
          <>
            <Box>
              {(historyData?.data ?? []).length === 0 && !historyFetching ? (
                <Box py={8} textAlign="center">
                  <HistoryIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.08)', mb: 1.5 }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.88rem' }}>
                    No history yet.
                  </Typography>
                  <Typography
                    sx={{ color: 'rgba(255,255,255,0.18)', fontSize: '0.75rem', mt: 0.5 }}
                  >
                    Start recording purchases to see stock movements here.
                  </Typography>
                </Box>
              ) : (
                (historyData?.data ?? []).map((event: any, idx: number) => {
                  const meta = EVENT_META[event.event_type] ?? {
                    label: event.event_type,
                    color: '#9e9e9e',
                    icon: null,
                  };
                  const positive = event.quantity_change > 0;
                  return (
                    <Box
                      key={event.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        px: 3,
                        py: 1.75,
                        borderBottom:
                          idx < (historyData?.data ?? []).length - 1
                            ? '1px solid rgba(255,255,255,0.04)'
                            : 'none',
                        '&:hover': { bgcolor: 'rgba(255,152,0,0.02)' },
                      }}
                    >
                      {/* Event type dot */}
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: `${meta.color}18`,
                          color: meta.color,
                          flexShrink: 0,
                        }}
                      >
                        {meta.icon}
                      </Box>

                      {/* Description */}
                      <Box flex={1} minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            label={meta.label}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              bgcolor: `${meta.color}18`,
                              color: meta.color,
                              border: `1px solid ${meta.color}30`,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}
                          >
                            {event.description}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2} mt={0.3}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem' }}
                          >
                            {event.event_date
                              ? format(new Date(event.event_date), 'dd MMM yyyy')
                              : '—'}
                          </Typography>
                          {event.created_by && (
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(255,255,255,0.22)', fontSize: '0.7rem' }}
                            >
                              {event.created_by}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Quantity delta */}
                      <Typography
                        variant="body2"
                        fontWeight={800}
                        sx={{
                          color: positive ? '#66bb6a' : '#ef5350',
                          fontFamily: 'monospace',
                          fontSize: '0.9rem',
                          flexShrink: 0,
                        }}
                      >
                        {positive ? '+' : ''}
                        {event.quantity_change}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
            <TablePagination
              component="div"
              count={historyData?.total ?? 0}
              page={historyPage}
              rowsPerPage={historyPageSize}
              rowsPerPageOptions={[10, 20, 50]}
              onPageChange={(_, p) => setHistoryPage(p)}
              onRowsPerPageChange={(e) => {
                setHistoryPageSize(parseInt(e.target.value, 10));
                setHistoryPage(0);
              }}
              sx={{
                color: 'rgba(255,255,255,0.4)',
                borderTop: '1px solid rgba(255,152,0,0.1)',
                fontSize: '0.78rem',
              }}
            />
          </>
        )}
      </Paper>

      {/* ══ Create / Edit Dialog ══ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: dialogPaper } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2.5,
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            background: 'linear-gradient(135deg, rgba(255,152,0,0.06) 0%, transparent 100%)',
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ color: '#ffffff' }}>
            {editRecord ? 'Edit Purchase' : 'Record Crate Purchase'}
          </Typography>
          {editRecord && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.38)' }}>
              {editRecord.purchased_date
                ? format(new Date(editRecord.purchased_date), 'dd MMM yyyy')
                : ''}
            </Typography>
          )}
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          {formError && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5,
                borderRadius: 2,
                bgcolor: 'rgba(211,47,47,0.1)',
                border: '1px solid rgba(211,47,47,0.3)',
                color: '#ffffff',
              }}
            >
              {formError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Bundles"
                type="number"
                fullWidth
                required
                size="small"
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                slotProps={{ htmlInput: { min: 1, step: 1 } }}
                helperText="1 bundle = 100 crates"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Unit Price (GH₵ / bundle)"
                type="number"
                fullWidth
                required
                size="small"
                value={form.unit_price}
                onChange={(e) => setForm((f) => ({ ...f, unit_price: e.target.value }))}
                slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Purchase Date"
                type="date"
                fullWidth
                required
                size="small"
                value={form.purchased_date}
                onChange={(e) => setForm((f) => ({ ...f, purchased_date: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo
                options={(suppliers as any[]).filter((s: any) => s.is_active)}
                getOptionLabel={(o: any) => (typeof o === 'string' ? o : o.name)}
                value={
                  form.supplier_id
                    ? ((suppliers as any[]).find((s: any) => s.id === form.supplier_id) ??
                      form.supplier)
                    : form.supplier
                }
                onChange={(_, val: any) => {
                  if (!val) {
                    setForm((f) => ({ ...f, supplier: '', supplier_id: null }));
                  } else if (typeof val === 'string') {
                    setForm((f) => ({ ...f, supplier: val, supplier_id: null }));
                  } else {
                    setForm((f) => ({ ...f, supplier: val.name, supplier_id: val.id }));
                  }
                }}
                onInputChange={(_, val, reason) => {
                  if (reason === 'input')
                    setForm((f) => ({ ...f, supplier: val, supplier_id: null }));
                }}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={form.is_credit ? 'Supplier (required)' : 'Supplier (optional)'}
                    required={form.is_credit}
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_credit}
                    onChange={(e) => setForm((f) => ({ ...f, is_credit: e.target.checked }))}
                    size="small"
                    color="warning"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem' }}
                  >
                    Credit purchase — payment owed to supplier
                  </Typography>
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Notes (optional)"
                fullWidth
                multiline
                rows={2}
                size="small"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>
            {!editRecord && (
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.skip_expenditure}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, skip_expenditure: e.target.checked }))
                      }
                      size="small"
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}
                    >
                      Add crates only — skip expenditure record
                    </Typography>
                  }
                />
              </Grid>
            )}

            {/* Live cost preview */}
            {form.quantity && form.unit_price && (
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    background: 'rgba(255,152,0,0.05)',
                    border: '1px solid rgba(255,152,0,0.15)',
                    borderRadius: 2,
                    display: 'flex',
                    gap: 3,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.35)',
                        fontSize: '0.62rem',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}
                    >
                      Crates
                    </Typography>
                    <Typography
                      fontWeight={800}
                      sx={{ color: '#ff9800', fontSize: '1.1rem', fontFamily: 'monospace' }}
                    >
                      {totalCrates.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.35)',
                        fontSize: '0.62rem',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}
                    >
                      Total Cost
                    </Typography>
                    <Typography
                      fontWeight={800}
                      sx={{ color: '#42a5f5', fontSize: '1.1rem', fontFamily: 'monospace' }}
                    >
                      GH₵ {totalCost.toFixed(2)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              createMutation.isPending ||
              createStockOnlyMutation.isPending ||
              updateMutation.isPending ||
              (form.is_credit && !form.supplier_id)
            }
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            {createMutation.isPending ||
            createStockOnlyMutation.isPending ||
            updateMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : editRecord ? (
              'Save Changes'
            ) : (
              'Record Purchase'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Delete Confirmation ══ */}
      <Dialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              backgroundImage: 'none',
              border: '1px solid rgba(244,67,54,0.25)',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2.5,
            borderBottom: '1px solid rgba(244,67,54,0.1)',
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ color: '#ef5350' }}>
            Delete Record?
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
            This will permanently remove this crate purchase record. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setDeleteId(null)}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, px: 3 }}
          >
            {deleteMutation.isPending ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrateStocks;
